import { Box, ClickAwayListener, styled } from '@mui/material';
import { iconButtonClasses } from '@mui/material/IconButton';
import { IconCross, IconSearch } from 'assets/img';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { LwIconButton, themeConfig } from 'redesign';
import { useToggle } from 'shared/hooks';
import { usePlanningParamsContext } from '../../../providers/planning-params-provider';

const EXPANDED_SEARCH_MIN_WIDTH_PX = 260;

export const PlanningToolboxSearch = () => {
  const { t } = useTranslation('ogp-screen-specific');
  const { searchInput, setSearchInput } = usePlanningParamsContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const hasSearchText = searchInput.trim().length > 0;
  const { on: folded, toggle, setOff: unfold, setOn: fold } = useToggle(!hasSearchText);

  useEffect(() => {
    if (hasSearchText) {
      unfold();
    }
  }, [hasSearchText, unfold]);

  useEffect(() => {
    if (folded) {
      return;
    }
    inputRef.current?.focus();
  }, [folded]);

  const handleClickAway = () => {
    if (hasSearchText) {
      return;
    }
    if (folded) {
      return;
    }
    fold();
  };

  if (folded) {
    return (
      <CollapsedSearchTrigger
        data-testid="planning-toolbox-search"
        onClick={toggle}
        iconColorMode="stroke"
        color="secondary"
        size="large"
        aria-label={t('planning.toolbox.search.open-aria-label')}
      >
        <IconWrapper>
          <IconSearch />
        </IconWrapper>
      </CollapsedSearchTrigger>
    );
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <ExpandedSearchField data-testid="planning-toolbox-search">
        <LeadingIcon aria-hidden>
          <StyledIconSearch />
        </LeadingIcon>
        <SearchTextInput
          ref={inputRef}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          aria-label={t('planning.toolbox.search.placeholder')}
          placeholder={t('planning.toolbox.search.placeholder')}
          type="text"
        />
        {searchInput ? (
          <ClearIconButton
            iconColorMode="stroke"
            color="default"
            size="small"
            aria-label={t('planning.toolbox.search.clear-aria-label')}
            onClick={() => setSearchInput('')}
            onMouseDown={(event) => event.preventDefault()}
          >
            <StyledIconCross />
          </ClearIconButton>
        ) : null}
      </ExpandedSearchField>
    </ClickAwayListener>
  );
};

const CollapsedSearchTrigger = styled(LwIconButton)({
  marginLeft: 'auto',
});

const IconWrapper = styled(Box)({
  display: 'flex',
  flexShrink: 0,
  height: themeConfig.icons.height,
  width: themeConfig.icons.width,
});

const ExpandedSearchField = styled(Box)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.lwWhite[100],
  border: `1px solid ${theme.palette.lwPrimaryAccentDark[20]}`,
  borderRadius: theme.spacing(10),
  display: 'flex',
  gap: theme.spacing(2),
  minWidth: `min(${EXPANDED_SEARCH_MIN_WIDTH_PX}px, 100%)`,
  padding: theme.spacing(2, 4, 2, 3),
  width: '100%',
  '&:focus-within': {
    borderColor: theme.palette.lwPrimaryAccentLight[100],
    boxShadow: `0 0 0 4px ${theme.palette.lwPrimaryAccentLight[10]}`,
  },
}));

const LeadingIcon = styled(Box)({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  height: '24px',
  justifyContent: 'center',
  width: '24px',
});

const SearchTextInput = styled('input')(({ theme }) => ({
  backgroundColor: 'transparent',
  border: 'none',
  color: theme.palette.lwBlack[100],
  flex: '1 1 0%',
  fontFamily: theme.typography.body2.fontFamily,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightMedium,
  lineHeight: theme.typography.body2.lineHeight,
  margin: 0,
  minWidth: 0,
  outline: 'none',
  padding: 0,
  width: '100%',
}));

const ClearIconButton = styled(LwIconButton)(({ theme }) => ({
  flexShrink: 0,
  marginRight: theme.spacing(-1),
  [`&.${iconButtonClasses.root}:hover`]: {
    backgroundColor: 'transparent',
  },
}));

const StyledIconSearch = styled(IconSearch)(({ theme }) => ({
  path: {
    stroke: `${theme.palette.lwPrimaryAccentDark[60]}`,
  },
}));

const StyledIconCross = styled(IconCross)(({ theme }) => ({
  path: {
    stroke: `${theme.palette.lwPrimaryAccentDark[60]}`,
  },
}));
